export const GA4_INTEGRATION_ID = 168;
export const GA4_PROVIDER_ID = 44;
export const SNOWFLAKE_INTEGRATION_ID = 183;
export const SNOWFLAKE_PROVIDER_ID = 72;

/**
 * Calculated-metric custom-import operands always carry providerId=26
 * (see the BE Joi schema for PLACEHOLDER operands).
 */
export const CUSTOM_IMPORT_PROVIDER_ID = 26;

/**
 * BE sentinel in `accountContentId`: the Snowflake mapping covers all warehouse data
 * rather than one view (no real view has id -1), so the dropdown must round-trip it.
 */
export const SNOWFLAKE_NO_VIEW_SENTINEL = -1;

/**
 * UI-only mapping type: the backend stores Snowflake mappings as
 * type=ANALYTICS + providerId=72, but the UI shows a separate radio button.
 */
export const SNOWFLAKE_UI_MAPPING_TYPE = 'SNOWFLAKE' as const;

export const PROVIDER_OPTIONS = [
  { id: GA4_PROVIDER_ID, label: 'GA4' },
  { id: SNOWFLAKE_PROVIDER_ID, label: 'Snowflake' },
] as const;

const PARAM_NAME_OVERRIDES: Record<string, string> = {
  BUYERTYPE: 'Buyer Type',
  BUYERTYPECANCELLED: 'Buyer Type Cancelled',
  SOURCEMEDIUM: 'Source Medium',
};

/**
 * Turns camelCase / SCREAMING_SNAKE parameter names into readable labels;
 * PARAM_NAME_OVERRIDES covers compound words the regex cannot split.
 */
export function formatParamName(s: string): string {
  if (PARAM_NAME_OVERRIDES[s]) return PARAM_NAME_OVERRIDES[s];
  return s
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * GA4 also matches presets carrying no provider at all (legacy rows predate the
 * providerId column); every other provider must match exactly.
 */
export function filterPresetsByProvider<T extends { mapping?: { providerId?: number | null } | null }>(
  presets: T[],
  selectedProviderId: number | null,
): T[] {
  if (!selectedProviderId) return presets;
  return presets.filter((preset) => {
    const presetProviderId = preset.mapping?.providerId;
    if (selectedProviderId === GA4_PROVIDER_ID) {
      return !presetProviderId || presetProviderId === GA4_PROVIDER_ID;
    }
    return presetProviderId === selectedProviderId;
  });
}

/**
 * No inference needed: a BE migration guarantees ANALYTICS mappings always carry providerId.
 */
export function detectProviderFromMapping(
  mapping?: {
    providerId?: number | null;
    accountContentId?: number | null;
    type?: string | null;
  } | null,
): number | null {
  return mapping?.providerId ?? null;
}

/**
 * ANALYTICS mappings submitted without an explicit provider default to GA4
 * (the most common case); other types pass through unchanged.
 */
export function resolveProviderIdForSubmission(
  type: string | undefined | null,
  providerId: number | undefined | null,
): number | undefined | null {
  if (type === 'ANALYTICS') return providerId ?? GA4_PROVIDER_ID;
  return providerId;
}

/**
 * Maps backend ANALYTICS + Snowflake provider onto the UI-only SNOWFLAKE radio
 * type; all other types pass through unchanged.
 */
export function getUiMappingType(
  type: string | null | undefined,
  providerId: number | null | undefined,
): string | null | undefined {
  if (type === 'ANALYTICS' && providerId === SNOWFLAKE_PROVIDER_ID) {
    return SNOWFLAKE_UI_MAPPING_TYPE;
  }
  return type;
}

/**
 * Keeps the value while options are still loading (empty list) so the form doesn't clobber
 * itself before the integration query resolves; the Snowflake -1 sentinel also survives.
 */
export function resolveAccountContentIdForDropdown(
  currentAccountContentId: number | null | undefined,
  options: ReadonlyArray<{ id: string | number }>,
  isSnowflake: boolean,
): string {
  if (currentAccountContentId == null) return '';
  const asString = String(currentAccountContentId);

  if (isSnowflake && currentAccountContentId === SNOWFLAKE_NO_VIEW_SENTINEL) return asString;

  if (!options.length) return asString;

  return options.some((opt) => opt.id.toString() === asString) ? asString : '';
}

/**
 * The BE rejects the save when a metric's `optimization_target_type` omits the funnel step
 * type ("Mismatch between funnel step type and metric type", BUG-012) — all providers.
 */
export function filterMeasurementsByFunnelStep<T extends { optimization_target_type?: string[] | null }>(
  measurements: T[] | null | undefined,
  funnelStepType: string,
  _selectedProviderId: number | null,
): T[] {
  if (!measurements) return [];
  return measurements.filter((m) => m.optimization_target_type?.includes(funnelStepType));
}

/**
 * utm_* parameters are only received and reported through GA4, so no other provider
 * can source Analytics data for the account picker.
 */
export function filterGa4AccountContents<T extends { provider?: { provider_id?: number | null } | null }>(
  contents: T[] | null | undefined,
): T[] {
  if (!contents) return [];
  return contents.filter((c) => c.provider?.provider_id === GA4_PROVIDER_ID);
}

/**
 * Snowflake mappings persist `accountContentId` as a nexoya content_id, so the picker must
 * source options from `listAccountContents`, not `integration.filterOptions.filterList`.
 */
export function filterSnowflakeAccountContents<T extends { provider?: { provider_id?: number | null } | null }>(
  contents: T[] | null | undefined,
): T[] {
  if (!contents) return [];
  return contents.filter((c) => c.provider?.provider_id === SNOWFLAKE_PROVIDER_ID);
}
